import type { User } from '@/api/users';
import type { StatusTone } from '@/components/app';
import { TASHKENT_TZ } from '@/lib/datetime';

type DateLike = Date | string | null | undefined;

function toDate(value: DateLike): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** «30.10.2026» по Ташкенту; пусто — «—». */
export function formatDate(value: DateLike): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TASHKENT_TZ }).format(d);
}

/** «30.10.2026, 14:07» по Ташкенту; пусто — «—». */
export function formatDateTime(value: DateLike): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TASHKENT_TZ,
  }).format(d);
}

/** Имя для заголовков: «Имя Фамилия», иначе логин. */
export function userDisplayName(user: Pick<User, 'firstName' | 'lastName' | 'username'>): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username;
}

/** Короткое имя для текстов подтверждений (как раньше: логин → email → начало id). */
export function userShortName(user: Pick<User, 'username' | 'email' | 'id'>): string {
  return user.username || user.email || user.id.slice(0, 8);
}

export function userInitials(user: Pick<User, 'firstName' | 'lastName' | 'username'>): string {
  const parts = userDisplayName(user).split(/[\s._@-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] ?? '?').slice(0, 1);
  return letters.toUpperCase();
}

/** Сколько полных дней осталось до даты (вверх); null — даты нет или она прошла. */
export function daysLeft(value: DateLike, now: Date = new Date()): number | null {
  const d = toDate(value);
  if (!d) return null;
  const ms = d.getTime() - now.getTime();
  return ms > 0 ? Math.ceil(ms / 86_400_000) : null;
}

export interface UserStatus {
  label: string;
  tone: StatusTone;
}

/** Статус аккаунта для таблицы и карточки: удалён > заблокирован > активен. */
export function userStatus(user: Pick<User, 'isDeleted' | 'isBlocked'>): UserStatus {
  if (user.isDeleted) return { label: 'Удалён', tone: 'neutral' };
  if (user.isBlocked) return { label: 'Заблокирован', tone: 'neg' };
  return { label: 'Активен', tone: 'pos' };
}
